import { inject, Injectable, signal } from '@angular/core';
import { RequesterService } from './requester.service';
import { SERVICE_CONFIG } from '../constants';
import { BehaviorSubject, interval, Observable, of, Subject, Subscription } from 'rxjs';
import { ExternalTokenInterface, IDHubTokenInterface } from '../interfaces/token.interface';
import { map, takeUntil } from 'rxjs/operators';
import { TOKEN } from '../constants/token.constant';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { RedirectType } from '../enums/redirect-type.enum';
import { ScenarioState } from '../enums/scenario.enum';
import { LanguageService } from './language.service';
import { countDiff, setDisplayParamTokenValue } from '../utils/auth-utils';
import { languageContext, ProcessTypeEnum } from '../enums';
import { ThemeService } from './theme.service';
import { environment } from '../../../environments/environment';
import { DeviceDetectorService } from 'ngx-device-detector';
import { PendingChangesService } from '../../shared/services/pending-changes.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly requesterService = inject(RequesterService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly languageService = inject(LanguageService);
  private readonly themeService = inject(ThemeService);
  private readonly pendingChangesService = inject(PendingChangesService);
  private readonly _processType = signal<ProcessTypeEnum | null>(null);

  readonly processType = this._processType.asReadonly();

  stopInterval$: Subject<boolean> = new Subject<boolean>();
  timeToExpiration$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  expireTimer$: Subscription = new Subscription();
  sessionExpireMessageKey = 'warning.sessionExpiredIn';
  showBadUrlErrorIfExists = true;
  childProcessFinished$ = new Subject<boolean>();
  isMobile = inject(DeviceDetectorService).isMobile(navigator.userAgent);

  auth(
    oneTimeToken: string,
    processType: string
  ): Observable<ExternalTokenInterface | IDHubTokenInterface> {
    const browserLang = navigator.language.split('-')[0];
    return this.requesterService.request(SERVICE_CONFIG.auth, { oneTimeToken, processType }, null, {
      skipToken: true,
      headers: {
        language: browserLang.toUpperCase(),
      },
    });
  }

  stopPolling(): void {
    this.stopInterval$.next(true);
  }

  setToken(token: string, external: boolean): void {
    this.requesterService.setToken(token, external);
  }

  serveOneTimeToken(
    ott: string,
    processType: string,
    path: string,
    external: boolean
  ): Observable<string | null> {
    return this.auth(ott, processType).pipe(
      map((tokenResponse) => {
        const tokenValue = tokenResponse.webToken.id;
        this.themeService.update(tokenResponse.templateId);
        this.setToken(tokenValue, external);
        this.setLocalStorage(tokenResponse, external);
        this.setLanguages(
          path,
          external,
          tokenResponse.currentLanguage,
          tokenResponse.allowedLanguages
        );
        this.location.replaceState(this.router.createUrlTree([path]).toString());
        return tokenValue;
      })
    );
  }

  private setLocalStorage(token: IDHubTokenInterface | ExternalTokenInterface, external: boolean) {
    const exp = token.expiringAt.toString();
    sessionStorage.setItem(TOKEN.TEMPLATE_ID, token.templateId || '');
    if (external) {
      localStorage.setItem(TOKEN.EXTERNAL_JWT, token.webToken.id);
      localStorage.setItem(TOKEN.EXTERNAL_EXPIRING_AT, exp);

      const t: ExternalTokenInterface = token as ExternalTokenInterface;

      if (t.failSafeRedirectUrl && t.failSafeRedirectUrl.url) {
        localStorage.setItem(TOKEN.EXTERNAL_SUCCESS_REDIRECT_URL, t.failSafeRedirectUrl.url);
        localStorage.setItem(TOKEN.EXTERNAL_FAILURE_REDIRECT_URL, t.failSafeRedirectUrl.url);
        localStorage.setItem(TOKEN.EXTERNAL_ERROR_REDIRECT_URL, t.failSafeRedirectUrl.url);
      }
    } else {
      localStorage.setItem(TOKEN.JWT, token.webToken.id);
      localStorage.setItem(TOKEN.EXPIRING_AT, exp);

      const t: IDHubTokenInterface = token as IDHubTokenInterface;

      if (t.failureRedirectUrl && t.failureRedirectUrl.url) {
        localStorage.setItem(TOKEN.FAILURE_REDIRECT_URL, t.failureRedirectUrl.url);
      }
      if (t.successRedirectUrl && t.successRedirectUrl.url) {
        localStorage.setItem(TOKEN.SUCCESS_REDIRECT_URL, t.successRedirectUrl.url);
      }
      if (t.errorRedirectUrl && t.errorRedirectUrl.url) {
        localStorage.setItem(TOKEN.ERROR_REDIRECT_URL, t.errorRedirectUrl.url);
      }

      if (t.displayParameters) {
        localStorage.setItem(
          TOKEN.SUCCESS_WINDOW_SHOW,
          setDisplayParamTokenValue(t.displayParameters.SUCCESS_WINDOW_SHOW)
        );
        localStorage.setItem(
          TOKEN.FAILURE_WINDOW_SHOW,
          setDisplayParamTokenValue(t.displayParameters.FAILURE_WINDOW_SHOW)
        );
        localStorage.setItem(
          TOKEN.ERROR_WINDOW_SHOW,
          setDisplayParamTokenValue(t.displayParameters.ERROR_WINDOW_SHOW)
        );
        localStorage.setItem(
          TOKEN.WELCOME_SCREEN_SHOW,
          setDisplayParamTokenValue(t.displayParameters.WELCOME_SCREEN_SHOW)
        );
      }
    }
    this.serveTime(external);
  }

  private setLanguages(
    path: string,
    external: boolean,
    currentLanguage?: string,
    allowedLanguages?: string[]
  ) {
    this.languageService.initLanguageContext({
      path,
      currentLanguage: currentLanguage,
      allowedLanguages: allowedLanguages,
      context: external ? languageContext.EXTERNAL : languageContext.IDHUB,
    });
  }

  clearToken(external: boolean) {
    for (const key of Object.keys(TOKEN)) {
      if (!localStorage.length) {
        break;
      }
      const tokenValue = TOKEN[key];
      if (external) {
        if (key.startsWith('EXTERNAL')) {
          localStorage.removeItem(tokenValue);
        }
      } else {
        if (!key.startsWith('EXTERNAL')) {
          localStorage.removeItem(tokenValue);
        }
      }
    }
  }

  redirectToType(type: RedirectType, external: boolean, isChild: boolean = false) {
    const redirectUrl = this.getRedirectUrl(type, external);
    if (redirectUrl) {
      this.redirect(external, redirectUrl, isChild);
    }
  }

  redirectByScenarioState(state: ScenarioState): void {
    switch (state) {
      case ScenarioState.SCENARIO_SUCCESS:
        return this.redirectToType(RedirectType.success, false);
      case ScenarioState.SCENARIO_ERROR:
        return this.redirectToType(RedirectType.error, false);
      case ScenarioState.SCENARIO_FAILED:
        return this.redirectToType(RedirectType.failure, false);
      default:
        return;
    }
  }

  getStepVisibility(state: ScenarioState): boolean {
    switch (state) {
      case ScenarioState.SCENARIO_SUCCESS:
        return localStorage.getItem(TOKEN.SUCCESS_WINDOW_SHOW) === '1';
      case ScenarioState.SCENARIO_ERROR:
        return localStorage.getItem(TOKEN.ERROR_WINDOW_SHOW) === '1';
      case ScenarioState.SCENARIO_FAILED:
        return localStorage.getItem(TOKEN.FAILURE_WINDOW_SHOW) === '1';
      default:
        return true;
    }
  }

  getRedirectByScenarioState(state: ScenarioState, external: boolean): string | null {
    switch (state) {
      case ScenarioState.SCENARIO_SUCCESS:
        return this.getRedirectUrl(RedirectType.success, external);
      case ScenarioState.SCENARIO_ERROR:
        return this.getRedirectUrl(RedirectType.error, external);
      case ScenarioState.SCENARIO_FAILED:
        return this.getRedirectUrl(RedirectType.error, external);
      default:
        return null;
    }
  }

  getRedirectUrl(type: RedirectType, external: boolean): string | null {
    if (external) {
      switch (type) {
        case RedirectType.success:
          return localStorage.getItem(TOKEN.EXTERNAL_SUCCESS_REDIRECT_URL);
        case RedirectType.error:
          return localStorage.getItem(TOKEN.EXTERNAL_ERROR_REDIRECT_URL);
        case RedirectType.failure:
          return localStorage.getItem(TOKEN.EXTERNAL_FAILURE_REDIRECT_URL);
        default:
          return null;
      }
    }
    switch (type) {
      case RedirectType.success:
        return localStorage.getItem(TOKEN.SUCCESS_REDIRECT_URL);
      case RedirectType.error:
        return localStorage.getItem(TOKEN.ERROR_REDIRECT_URL);
      case RedirectType.failure:
        return localStorage.getItem(TOKEN.FAILURE_REDIRECT_URL);
      default:
        return null;
    }
  }

  serveStoredWebToken(external: boolean, refresh = false): Observable<string | null> {
    const tokenKey = external ? TOKEN.EXTERNAL_JWT : TOKEN.JWT;
    const jwt = localStorage.getItem(tokenKey);
    if (jwt) {
      if (!refresh) {
        this.serveTime(external);
      }
      this.setToken(jwt, external);
      return of(jwt);
    }
    return of(null);
  }

  stopTimer() {
    this.expireTimer$?.unsubscribe();
    this.timeToExpiration$.next(0);
  }

  clearTokenKey(tokenKey: string) {
    localStorage.removeItem(tokenKey);
  }

  setProcessType(processType: ProcessTypeEnum): void {
    this._processType.set(processType);
  }

  private serveTime(external: boolean) {
    const expKey = external ? TOKEN.EXTERNAL_EXPIRING_AT : TOKEN.EXPIRING_AT;
    const expValue = Number(localStorage.getItem(expKey));

    if (expValue) {
      const end = new Date(expValue);
      const endTime = end.getTime();
      let diffInSeconds = countDiff(endTime);

      if (diffInSeconds <= 0) {
        this.redirectToType(RedirectType.error, external);
      }

      this.expireTimer$ = interval(1000)
        .pipe(
          takeUntil(this.stopInterval$),
          map(() => {
            diffInSeconds = countDiff(endTime);
            if (diffInSeconds <= 0) {
              this.showBadUrlErrorIfExists = false;
              this.timeToExpiration$.next(0);
              this.stopInterval$.next(true);
              return;
            }
            this.timeToExpiration$.next(diffInSeconds);

            // if (!environment.production) {
            //   // tslint:disable-next-line:no-console
            //   console.log(
            //     'pozostały czas w sekundach',
            //     diffInSeconds,
            //     ' teraz: ',
            //     new Date().toLocaleTimeString(),
            //     ' koniec: ',
            //     end.toLocaleTimeString(),
            //     ' token: ',
            //     expValue,
            //     // ' toaster widoczny: ',
            //     // timeToExpirationVisible
            //   );
            // }
          })
        )
        .subscribe({
          complete: () => {
            this.finishedTimerHandler(external);
          },
        });
    }
  }



  private finishedTimerHandler(external: boolean) {
    const isChildProcess =
      this.processType() === ProcessTypeEnum.child;

    if (this.isMobile && isChildProcess) {
      this.childProcessFinished$.next(true);
    } else {
      this.redirectToType(RedirectType.error, external);
    }
  }

  private redirect(external: boolean, redirectUrl: string | null, isChild: boolean) {
    // TODO - check if redirectUrl include domain
    // TODO - use Angular Router navigation instead of window.location.href
    if (!redirectUrl || isChild) return;
    this.pendingChangesService.allowNextRedirect();
    this.clearToken(external);
    this.stopInterval$.unsubscribe();
    window.location.href = redirectUrl;
  }
}
